import { useCallback, useMemo } from 'react';
import { keyBy } from 'lodash';
import { useParams } from 'react-router-dom';
import throttledQueue from 'throttled-queue';

import { isUsernameOrg } from '@inspiren-monorepo/util-users';

import { getUsersTableFields } from './helpers/getUsersTableFields';
import useUsersUpsert from './hooks/useUsersUpsert';

import { useBuildingOptions } from '../../../../hooks/useBuildingOptions';
import { useIsAdmin } from '../../../../hooks/useIsAdmin';
import { useOrganizationRolesMap } from '../../../../hooks/useOrganizationRolesMap';
import { useUnitOptions } from '../../../../hooks/useUnitOptions';
import ImportTable from '../ImportTable';
import { mapCsvRowsToFields } from '../ImportTable/helpers/mapCsvRowsToFields';
import csvCellMappers from '../ImportTable/utils/csvCellMappers';

import type { UserFields } from './helpers/getUsersTableFields';
import type { DataFields } from '../../types/DataFields';
import type {
  FieldsConfiguration,
  Importable,
} from '../ImportTable/types/importable';
import type { UploadFn } from '../ImportTable/types/uploadFn';

const throttle = throttledQueue(3, 1200, true);

const UsersImportTable = () => {
  const { org } = useParams<{ org: string }>();
  const { isAdmin } = useIsAdmin();

  const usernameOrg = isUsernameOrg(org);

  const { data: buildings, isLoading: isLoadingBuildings } = useBuildingOptions(
    {
      refetchOnMount: false,
      refetchOnWindowFocus: false,
    },
  );

  const { data: units, isLoading: isLoadingUnits } = useUnitOptions({
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const buildingsMap = useMemo(() => keyBy(buildings || [], 'id'), [buildings]);
  const unitsMap = useMemo(() => keyBy(units || [], 'id'), [units]);

  const { data: roleMap = {}, isLoading: isLoadingRoles } =
    useOrganizationRolesMap(org);

  const fieldsConfiguration: FieldsConfiguration<UserFields> = useMemo(
    () => ({
      firstName: {
        csvHeader: 'first name',
        csvHeaderRegExp: /^first( name)?$/i,
        mapper: csvCellMappers.String,
      },
      lastName: {
        csvHeader: 'last name',
        csvHeaderRegExp: /^last( name)?$/i,
        mapper: csvCellMappers.String,
      },
      username: usernameOrg
        ? {
            csvHeader: 'username',
            mapper: csvCellMappers.String,
          }
        : undefined,
      email: {
        csvHeader: 'email',
        mapper: csvCellMappers.String,
      },
      role: {
        csvHeader: 'role',
        mapper: csvCellMappers.Role(roleMap),
      },
      password: {
        csvHeader: 'password',
        mapper: csvCellMappers.String,
      },
      levelAccess: {
        csvHeader: 'level access',
        mapper: csvCellMappers.LevelAccess,
      },
      unitId: {
        csvHeader: 'current unit',
        csvHeaderRegExp: /^(current )?unit$/i,
        mapper: csvCellMappers.Unit(units),
      },
    }),
    [roleMap, units, org],
  );

  const { handleAddSubmit } = useUsersUpsert({
    orgId: org,
    roleMap,
    unitsMap,
  });

  const columns: DataFields<UserFields> = useMemo(
    () =>
      getUsersTableFields(isAdmin, roleMap, buildingsMap, unitsMap, org).filter(
        (e) => !!fieldsConfiguration[e.field],
      ),
    [isAdmin, roleMap, org, fieldsConfiguration, unitsMap, buildingsMap],
  );

  const mapCsvToFields = useCallback(
    async (csvRows: string[][]): Promise<Importable<UserFields>[]> =>
      mapCsvRowsToFields(csvRows, fieldsConfiguration, () => false),
    [fieldsConfiguration],
  );

  const createDefaultRow = useCallback(
    (): UserFields =>
      ({
        id: '',
        firstName: '',
        lastName: '',
        email: '',
        username: '',
        role: '',
        password: '',
        levelAccess: undefined,
        levelAccessBuildingIds: [],
        unitId: '',
        beacon: '',
        orgId: org,
      }) as unknown as UserFields,
    [org],
  );

  const upload: UploadFn<UserFields> = useCallback(
    async (item) => {
      const {
        $meta: { alreadyExists },
        ...rest
      } = item;

      if (alreadyExists) {
        throw new Error('Users bulk update is not supported');
      }

      await throttle(async () => handleAddSubmit({ ...rest, org }, false));
    },
    [handleAddSubmit],
  );

  return (
    <ImportTable
      fields={columns}
      createDefaultRow={createDefaultRow}
      mapCsvToFields={mapCsvToFields}
      loading={isLoadingRoles || isLoadingUnits || isLoadingBuildings}
      upload={upload}
      itemName='users'
      heading={`Import users for org ${org}`}
    />
  );
};

export default UsersImportTable;
